// Validación de los dos formularios de Excepciones — zod, del lado del front.
//
// El backend también valida (y debe), pero eso no alcanza: descubrir que la justificación era
// obligatoria mediante un 400, después de haber redactado el caso completo, es una forma cara de
// enterarse. Estas reglas existen para que el error aparezca mientras se escribe.
//
// Los mínimos NO son decorativos y son A PROPÓSITO más estrictos que los del silo (40 contra 20 en
// la justificación, 20 contra 10 en la resolución). Una justificación de 20 caracteres cabe en "se
// equivocó el vendedor", que no explica nada y deja la auditoría igual de ciega que si estuviera
// vacía. Pedir de más antes de enviar educa; pedir de menos solo mueve el rechazo al servidor.
//
// 🔴 LOS MÁXIMOS ESTABAN AL REVÉS. El front aceptaba 2000 caracteres donde el silo corta en 1000, y
// 1000 en la acción donde el silo corta en 500: el operador redactaba el caso entero, le daba
// enviar y se llevaba un 400 opaco. Un mínimo más estricto que el backend es una decisión; un
// máximo más laxo es un bug.
import { z } from 'zod';

/** Los cinco tipos del corte, con su etiqueta. `autorizacion_tope` quedó fuera. */
export const EXCEPTION_TYPES = [
    { value: 'CAPTURE_FIX', label: 'Corrección de captura' },
    { value: 'LATE_CANCEL', label: 'Cancelación tardía' },
    { value: 'BACKDATING', label: 'Fecha retroactiva' },
    { value: 'PRICE_OVERRIDE', label: 'Precio forzado' },
    { value: 'EXTENDED_WARRANTY', label: 'Garantía extendida' },
] as const;

export const MIN_JUSTIFICATION = 40;
export const MIN_RESOLUTION = 20;
/** El silo exige 3 en el cuerpo de una nota; el front lo espeja para no mandar un 400 evitable. */
export const MIN_NOTE = 3;

/** Topes del silo (`class-validator`). Pasarse de acá es 400 seguro. */
const MAX_JUSTIFICATION = 1000;
const MAX_RESOLUTION = 1000;
const MAX_ACTION = 500;

export const createExceptionSchema = z
    .object({
        type: z.enum([
            'CAPTURE_FIX',
            'LATE_CANCEL',
            'BACKDATING',
            'PRICE_OVERRIDE',
            'EXTENDED_WARRANTY',
        ]),
        saleId: z.string().trim().max(64, 'El id de la venta no puede pasar de 64 caracteres.'),
        storeId: z.string().trim().max(64, 'El id de la tienda no puede pasar de 64 caracteres.'),
        /**
         * Quién PIDIÓ la excepción, cuando no es quien la registra — el campo «Solicitante» del
         * mockup, que faltaba entero.
         *
         * El botón vive en la pantalla del Admin VL, pero el caso normal es que el vendedor avise
         * por fuera y el Admin la anote. Sin este campo la columna «Solicitante» dice SIEMPRE el
         * Admin, y se pierde quién originó el caso — que es justo lo que la bandeja viene a
         * auditar. Vacío ⇒ el silo usa el actor del token.
         */
        requestedBy: z
            .string()
            .trim()
            .max(64, 'El identificador del solicitante no puede pasar de 64 caracteres.'),
        justification: z
            .string()
            .trim()
            .min(
                MIN_JUSTIFICATION,
                `Explica POR QUÉ se necesita la excepción (mínimo ${String(MIN_JUSTIFICATION)} caracteres). Es lo que va a leer quien la apruebe y quien la audite.`,
            )
            .max(
                MAX_JUSTIFICATION,
                `La justificación no puede pasar de ${String(MAX_JUSTIFICATION)} caracteres.`,
            ),
        requestedAction: z
            .string()
            .trim()
            .min(10, 'Di qué hay que hacer si se aprueba.')
            .max(MAX_ACTION, `La acción no puede pasar de ${String(MAX_ACTION)} caracteres.`),
        /** En PESOS: es lo que teclea el operador. La conversión a centavos la hace el submit. */
        impactPesos: z
            .string()
            .trim()
            .refine((v) => v === '' || Number.isFinite(Number(v)), 'Escribe un monto válido.'),
    })
    // El silo pide tienda O venta, no las dos: una excepción puede colgar de una venta puntual o de
    // toda una tienda. El front exigía `storeId` siempre, así que el caso «solo la venta» era
    // inalcanzable desde la UI aunque el backend lo aceptara.
    .superRefine((values, ctx) => {
        if (values.storeId.length === 0 && values.saleId.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['storeId'],
                message: 'Elige la tienda donde ocurrió, o liga la excepción a una venta.',
            });
        }
    });

export type CreateExceptionForm = z.infer<typeof createExceptionSchema>;

/** El formulario en blanco. Vive acá para que «Duplicar» pueda sembrarlo campo por campo. */
export const EMPTY_CREATE_FORM: CreateExceptionForm = {
    type: 'CAPTURE_FIX',
    saleId: '',
    storeId: '',
    requestedBy: '',
    justification: '',
    requestedAction: '',
    impactPesos: '',
};

export const resolveExceptionSchema = z.object({
    resolution: z
        .string()
        .trim()
        .min(
            MIN_RESOLUTION,
            `Escribe la resolución (mínimo ${String(MIN_RESOLUTION)} caracteres). Queda en la bitácora de auditoría como el fundamento de la decisión.`,
        )
        .max(
            MAX_RESOLUTION,
            `La resolución no puede pasar de ${String(MAX_RESOLUTION)} caracteres.`,
        ),
});

export type ResolveExceptionForm = z.infer<typeof resolveExceptionSchema>;

/**
 * Pesos tecleados → centavos enteros. El signo se conserva: hay impactos negativos.
 *
 * Devuelve `undefined` cuando no se tecleó nada, y NO `0`: el silo declara `impactCents` opcional, y
 * mandar un cero explícito afirmaría que la excepción no mueve dinero cuando lo cierto es que no se
 * capturó. En la tabla ese cero inventado es indistinguible de un cero real.
 */
export function impactToCents(pesos: string): number | undefined {
    const raw = pesos.trim();
    if (raw === '') return undefined;
    const value = Number(raw);
    return Number.isFinite(value) ? Math.round(value * 100) : undefined;
}
